/**
 * ============================================================================
 * 🎵 HEAREN iOS AUDIO SESSION MANAGER - BUILD 69
 * ============================================================================
 * 
 * iOS only allows ONE active AudioSession at a time.
 * This manager ensures that:
 * - Only one audio engine is active at any time
 * - Switching from Radio to Therapy (or vice versa) properly releases the previous session
 * - AudioContext is shared or properly handed off between services
 * - BUILD 52.8: Audio continues when screen is locked (background playback)
 * - BUILD 55: Auto-resume audio when app returns to foreground on iOS
 * - BUILD 67: WKWebView/Capacitor radio loop fix - lifecycle event protection
 * - BUILD 69: Explicit audio unlock for WKWebView (silent buffer trick)
 * 
 * Usage:
 *   import { audioSessionManager } from './audioSessionManager';
 *   
 *   // Before starting Radio:
 *   await audioSessionManager.acquireSession('radio');
 *   
 *   // Before starting Therapy:
 *   await audioSessionManager.acquireSession('therapy');
 *   
 *   // When stopping:
 *   audioSessionManager.releaseSession('radio');
 * ============================================================================
 */

import { backgroundAudioHelper } from './backgroundAudioHelper';

// iOS detection
const isIOSDevice = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || window.Capacitor !== undefined;
};

// BUILD 67: Detect if running in Capacitor native (WKWebView) vs Safari
const isCapacitorNative = () => {
  if (typeof window === 'undefined') return false;
  return window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
};

// BUILD 69: Global audio unlock state (persists across session manager lifecycle)
let globalAudioUnlocked = false;
let globalAudioContext = null;

class AudioSessionManager {
  constructor() {
    this.isIOS = isIOSDevice();
    this.isCapacitor = isCapacitorNative();
    this.currentOwner = null; // 'radio' | 'therapy' | 'soundscape' | 'sos_calm' | null
    this.listeners = new Map(); // owner -> { onInterrupted, onReleased }
    
    // BUILD 69: Audio unlock state
    this.audioUnlocked = globalAudioUnlocked;
    
    // Callbacks for each service to stop their audio
    this.stopCallbacks = {
      radio: null,
      therapy: null,
      soundscape: null,
      sos_calm: null
    };
    
    // BUILD 55: Callbacks for each service to RESUME their audio
    this.resumeCallbacks = {
      radio: null,
      therapy: null,
      soundscape: null,
      sos_calm: null
    };
    
    // BUILD 52.8: Track playing state per owner to prevent background stops
    this.playingState = {
      radio: false,
      therapy: false,
      soundscape: false,
      sos_calm: false
    };
    
    // BUILD 55: Track if user explicitly paused (to prevent auto-resume)
    this.userPausedState = {
      radio: false,
      therapy: false,
      soundscape: false,
      sos_calm: false
    };
    
    // BUILD 52.8: Track if app is in background
    this.isAppInBackground = false;
    
    // BUILD 67: Track if an owner is currently starting (to prevent lifecycle interference)
    this.startingState = {
      radio: false,
      therapy: false,
      soundscape: false,
      sos_calm: false
    };
    
    // BUILD 67: Startup grace period - ignore lifecycle events for this duration after start
    this.startupGracePeriodMs = 5000; // 5 seconds
    this.startupTimestamps = {
      radio: 0,
      therapy: 0,
      soundscape: 0,
      sos_calm: 0
    };
    
    console.log(`[AudioSessionManager] Initialized (iOS: ${this.isIOS}, Capacitor: ${this.isCapacitor}, audioUnlocked: ${this.audioUnlocked})`);
    
    // BUILD 52.8: Setup background state tracking
    this.setupBackgroundTracking();
  }
  
  // ============================================================================
  // BUILD 69: AUDIO UNLOCK FOR WKWEBVIEW
  // WKWebView requires explicit audio unlock in user gesture context
  // ============================================================================
  
  /**
   * BUILD 69: Unlock audio for WKWebView - MUST be called in user tap context
   * Plays a silent buffer to "unlock" the AudioContext for subsequent playback
   * @param {string} owner - 'radio' | 'therapy' | 'soundscape' (for logging)
   * @returns {Promise<boolean>} - true if unlocked successfully
   */
  async unlockAudioOnce(owner = 'unknown') {
    // Already unlocked - skip
    if (this.audioUnlocked || globalAudioUnlocked) {
      console.log(`[iOS-Unlock] alreadyUnlocked owner=${owner}`);
      return true;
    }
    
    // Not needed for non-Capacitor (Safari handles this automatically)
    if (!this.isCapacitor) {
      console.log(`[iOS-Unlock] skip - not Capacitor (Safari mode)`);
      this.audioUnlocked = true;
      globalAudioUnlocked = true;
      // V1.1.2: Also activate background keepalive on Safari iOS
      backgroundAudioHelper.activate();
      return true;
    }
    
    console.log(`[iOS-Unlock] start owner=${owner}`);
    
    try {
      // Create or reuse AudioContext
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!globalAudioContext) {
        globalAudioContext = new AudioContextClass();
      }
      const ctx = globalAudioContext;
      
      console.log(`[iOS-Unlock] AudioContext.state=${ctx.state}`);
      
      // Step 1: Resume AudioContext (must be in user gesture)
      if (ctx.state === 'suspended') {
        await ctx.resume();
        console.log(`[iOS-Unlock] AudioContext resumed -> state=${ctx.state}`);
      }
      
      // Step 2: Play a very short silent buffer (50ms)
      // This "unlocks" the audio hardware on iOS
      const sampleRate = ctx.sampleRate || 44100;
      const duration = 0.05; // 50ms
      const frameCount = Math.ceil(sampleRate * duration);
      const silentBuffer = ctx.createBuffer(1, frameCount, sampleRate);
      
      // Fill with silence (zeros) - already zero by default but be explicit
      const channelData = silentBuffer.getChannelData(0);
      for (let i = 0; i < frameCount; i++) {
        channelData[i] = 0;
      }
      
      // Create source and connect with gain=0 (truly silent)
      const source = ctx.createBufferSource();
      source.buffer = silentBuffer;
      
      const gainNode = ctx.createGain();
      gainNode.gain.value = 0; // Completely silent
      
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Play and wait for completion
      source.start(0);
      
      // Wait for the buffer to finish (50ms + small margin)
      await new Promise(resolve => setTimeout(resolve, 60));
      
      // Cleanup
      try {
        source.stop();
        source.disconnect();
        gainNode.disconnect();
      } catch (e) {
        // Ignore - may already be stopped
      }
      
      // Mark as unlocked
      this.audioUnlocked = true;
      globalAudioUnlocked = true;
      
      // Activate background audio keep-alive (in user gesture context)
      backgroundAudioHelper.activate();
      
      console.log(`[iOS-Unlock] success owner=${owner} AudioContext.state=${ctx.state}`);
      return true;
      
    } catch (error) {
      console.error(`[iOS-Unlock] failed owner=${owner} error=${error.message}`);
      return false;
    }
  }
  
  /**
   * BUILD 69: Check if audio is unlocked
   * @returns {boolean}
   */
  isAudioUnlocked() {
    return this.audioUnlocked || globalAudioUnlocked;
  }
  
  /**
   * BUILD 69: Get the shared AudioContext (for services that need it)
   * @returns {AudioContext|null}
   */
  getSharedAudioContext() {
    return globalAudioContext;
  }
  
  // ============================================================================
  // BUILD 67: STARTUP PROTECTION FOR WKWEBVIEW
  // ============================================================================
  
  /**
   * BUILD 67: Mark owner as starting (lifecycle events will be ignored)
   * @param {string} owner - 'radio' | 'therapy' | 'soundscape'
   */
  setStarting(owner, isStarting) {
    this.startingState[owner] = isStarting;
    if (isStarting) {
      this.startupTimestamps[owner] = Date.now();
      console.log(`[AudioSessionManager] 🚀 ${owner} STARTING - lifecycle protection ACTIVE`);
    } else {
      console.log(`[AudioSessionManager] ✅ ${owner} started - lifecycle protection cleared`);
    }
  }
  
  /**
   * BUILD 67: Check if owner is in startup grace period
   * @param {string} owner - 'radio' | 'therapy' | 'soundscape'
   * @returns {boolean} Whether to block lifecycle events
   */
  isInStartupGracePeriod(owner) {
    if (this.startingState[owner]) {
      return true;
    }
    const elapsed = Date.now() - this.startupTimestamps[owner];
    if (elapsed < this.startupGracePeriodMs) {
      console.log(`[AudioSessionManager] ⏳ ${owner} in grace period (${elapsed}ms < ${this.startupGracePeriodMs}ms)`);
      return true;
    }
    return false;
  }
  
  /**
   * BUILD 67: Check if lifecycle event should be blocked for any owner
   * On WKWebView/Capacitor, lifecycle events can fire spuriously during playback start
   * @param {string} eventName - Name of the lifecycle event for logging
   * @returns {string|null} Owner name if blocked, null if not blocked
   */
  shouldBlockLifecycleEvent(eventName = 'unknown') {
    if (!this.isCapacitor) {
      return null; // Safari web doesn't have this issue
    }
    
    for (const owner of ['radio', 'therapy', 'soundscape', 'sos_calm']) {
      if (this.isInStartupGracePeriod(owner)) {
        const elapsed = Date.now() - this.startupTimestamps[owner];
        console.log(`[LifecycleBlock] owner=${owner} event=${eventName} reason=startup_grace t=${elapsed}ms`);
        return owner;
      }
    }
    return null;
  }
  
  /**
   * BUILD 52.8/55/67: Setup app background state tracking
   * This helps us prevent stopping audio when the app goes to background
   * BUILD 55: Also triggers auto-resume when returning to foreground
   * BUILD 67: WKWebView protection - ignore spurious lifecycle events during startup
   */
  setupBackgroundTracking() {
    if (typeof document === 'undefined') return;
    
    // Track visibility changes
    document.addEventListener('visibilitychange', () => {
      const wasInBackground = this.isAppInBackground;
      this.isAppInBackground = document.hidden;
      const eventName = this.isAppInBackground ? 'visibilitychange:hidden' : 'visibilitychange:visible';
      
      // BUILD 67: Block lifecycle events during startup on WKWebView
      const blockedOwner = this.shouldBlockLifecycleEvent(eventName);
      if (blockedOwner) {
        return;
      }
      
      if (this.isAppInBackground) {
        // V1.1.1: Store playback position before background
        this._notifyBackground();
        const playingOwner = Object.entries(this.playingState).find(([, playing]) => playing);
        if (playingOwner) {
          console.log(`[AudioSessionManager] ${playingOwner[0]} playing - keeping session active`);
        }
      } else {
        // BUILD 55: App returning to foreground - trigger resume
        this.onForeground();
      }
    });
    
    // V1.1.1: pageshow — fires on back/forward cache restore (bfcache) and unlock on iOS Safari
    window.addEventListener('pageshow', (e) => {
      if (e.persisted || !this.isAppInBackground) {
        console.log(`[AudioSessionManager] pageshow (persisted=${e.persisted}) — triggering foreground`);
        this.isAppInBackground = false;
        this.onForeground();
      }
    });
    
    // V1.1.1: focus — extra signal for iOS Safari unlock
    window.addEventListener('focus', () => {
      if (this.isAppInBackground) {
        console.log('[AudioSessionManager] window.focus — triggering foreground');
        this.isAppInBackground = false;
        this.onForeground();
      }
    });
    
    // Also track page hide (more reliable on iOS)
    window.addEventListener('pagehide', (e) => {
      // BUILD 67: Block lifecycle events during startup on WKWebView
      const blockedOwner = this.shouldBlockLifecycleEvent('pagehide');
      if (blockedOwner) {
        return;
      }
      
      // V1.1.1: Store position before page hide
      this._notifyBackground();
      
      // Don't stop if audio is playing - iOS should keep it going
      const playingOwner = Object.entries(this.playingState).find(([, playing]) => playing);
      if (playingOwner) {
        console.log(`[AudioSessionManager] ${playingOwner[0]} playing - NOT stopping on pagehide`);
      }
    });
    
    // Setup Capacitor listener if available
    this.setupCapacitorListener();
  }
  
  /**
   * BUILD 52.8/55/67: Setup Capacitor app state listener
   * BUILD 67: Critical WKWebView fix - protect against spurious appStateChange events
   */
  async setupCapacitorListener() {
    try {
      const { App } = await import('@capacitor/app');
      App.addListener('appStateChange', ({ isActive }) => {
        const wasInBackground = this.isAppInBackground;
        this.isAppInBackground = !isActive;
        const eventName = isActive ? 'appStateChange:active' : 'appStateChange:background';
        
        // BUILD 67: CRITICAL - Block lifecycle events during startup on WKWebView
        const blockedOwner = this.shouldBlockLifecycleEvent(eventName);
        if (blockedOwner) {
          return;
        }
        
        if (!isActive) {
          // V1.1.1: Store position before background
          this._notifyBackground();
          const playingOwner = Object.entries(this.playingState).find(([, playing]) => playing);
          if (playingOwner) {
            console.log(`[AudioSessionManager] ${playingOwner[0]} playing - keeping audio in background`);
          }
        } else {
          // BUILD 55: App coming to foreground - trigger resume
          // Only auto-resume if was actually in background
          if (wasInBackground) {
            this.onForeground();
          }
        }
      });
      console.log('[AudioSessionManager] Capacitor listener registered (BUILD 67)');
    } catch (e) {
      // Capacitor not available (web mode)
    }
  }
  
  // ============================================================================
  // BUILD 55: FOREGROUND RESUME LOGIC
  // ============================================================================
  
  /**
   * BUILD 55: Get the owner that was playing before background
   * Priority: radio > therapy > soundscape (first one marked as playing)
   * @returns {string|null} Owner name or null if none playing
   */
  getActivePlayingOwner() {
    for (const owner of ['radio', 'therapy', 'soundscape', 'sos_calm']) {
      if (this.playingState[owner] === true) {
        return owner;
      }
    }
    return null;
  }
  
  /**
   * BUILD 55: Attempt to resume audio for a specific owner
   * @param {string} owner - 'radio' | 'therapy' | 'soundscape'
   * @returns {Promise<boolean>} Whether resume was successful
   */
  async resumeOwner(owner) {
    // Check if user explicitly paused - don't auto-resume
    if (this.userPausedState[owner]) {
      return false;
    }
    
    // Check if we have a resume callback for this owner
    const resumeCallback = this.resumeCallbacks[owner];
    if (!resumeCallback) {
      return false;
    }
    
    try {
      await resumeCallback();
      return true;
    } catch (error) {
      console.error(`[AudioSessionManager] resume failed for ${owner}:`, error);
      return false;
    }
  }
  
  /**
   * BUILD 55/67: Called when app returns to foreground
   * Attempts to resume any audio that was playing
   * BUILD 67: Added protection against spurious calls on WKWebView
   */
  async onForeground() {
    // BUILD 67: Double-check startup protection
    const blockedOwner = this.shouldBlockLifecycleEvent('onForeground');
    if (blockedOwner) {
      return;
    }
    
    // Re-activate background audio keep-alive if still playing
    if (this.isAnyPlaying()) {
      backgroundAudioHelper.ensureActive();
    }
    
    // V1.1.2: On iOS Safari, also try to resume AudioContext immediately
    // Safari may auto-resume AudioContext on unlock without needing gesture
    if (this.isIOS && !this.isCapacitor) {
      try {
        const ctx = globalAudioContext;
        if (ctx && ctx.state !== 'running') {
          console.log(`[AudioSessionManager] iOS Safari foreground: resuming AudioContext (state=${ctx.state})`);
          ctx.resume().catch(() => {});
        }
      } catch (e) { /* ignore */ }
    }
    
    // Find the owner that was playing
    const activeOwner = this.getActivePlayingOwner();
    
    if (!activeOwner) {
      return;
    }
    
    // BUILD 67: Check if this owner is in startup grace period
    if (this.isInStartupGracePeriod(activeOwner)) {
      return;
    }
    
    // Check if user explicitly paused
    if (this.userPausedState[activeOwner]) {
      return;
    }
    
    // Attempt resume
    await this.resumeOwner(activeOwner);
  }
  
  /**
   * BUILD 55: Register a resume callback for a service
   * This will be called when the app returns to foreground
   */
  registerResumeCallback(owner, callback) {
    this.resumeCallbacks[owner] = callback;
    console.log(`[AudioSessionManager] ▶️ Registered resume callback for: ${owner}`);
  }
  
  /**
   * BUILD 55: Unregister a resume callback
   */
  unregisterResumeCallback(owner) {
    this.resumeCallbacks[owner] = null;
  }
  
  /**
   * BUILD 55: Mark owner as user-paused (prevents auto-resume)
   */
  setUserPaused(owner, isPaused) {
    this.userPausedState[owner] = isPaused;
    console.log(`[AudioSessionManager] ${owner} userPaused: ${isPaused}`);
  }

  /**
   * V1.1.1: Register a background callback (called when going to background/lock).
   * Use this to store playback position before the AudioContext gets suspended.
   */
  registerBackgroundCallback(owner, callback) {
    if (!this._backgroundCallbacks) this._backgroundCallbacks = {};
    this._backgroundCallbacks[owner] = callback;
  }

  /**
   * V1.1.1: Notify all registered services that the app is going to background.
   * Each service should store its playback position.
   */
  _notifyBackground() {
    if (!this._backgroundCallbacks) return;
    for (const [owner, cb] of Object.entries(this._backgroundCallbacks)) {
      if (cb && this.playingState[owner]) {
        try { cb(); } catch (e) {
          console.warn(`[AudioSessionManager] background callback error for ${owner}:`, e);
        }
      }
    }
  }

  /**
   * Register a stop callback for a service
   * This will be called when another service needs the audio session
   */
  registerStopCallback(owner, callback) {
    this.stopCallbacks[owner] = callback;
    console.log(`[AudioSessionManager] Registered stop callback for: ${owner}`);
  }

  /**
   * Unregister a stop callback
   */
  unregisterStopCallback(owner) {
    this.stopCallbacks[owner] = null;
  }
  
  /**
   * BUILD 52.8: Set playing state for an owner
   * This is used to prevent stopping audio when app goes to background
   * @param {string} owner - 'radio' | 'therapy' | 'soundscape'
   * @param {boolean} isPlaying - Whether the owner is currently playing
   * @param {string} reason - Reason for the state change (e.g., 'user', 'cleanup', 'track_ended')
   */
  setPlaying(owner, isPlaying, reason = 'unknown') {
    const wasPlaying = this.playingState[owner];
    this.playingState[owner] = isPlaying;
    
    // Log state change with reason
    if (wasPlaying !== isPlaying) {
      console.log(`[AudioSessionManager] 🎵 ${owner} playing: ${wasPlaying} → ${isPlaying} (reason: ${reason})`);
    } else {
      console.log(`[AudioSessionManager] ${owner} playing state unchanged: ${isPlaying} (reason: ${reason})`);
    }
    
   // Manage background audio keep-alive
   if (!isPlaying && !this.isAnyPlaying()) {
     if (window.__HEAREN_NATIVE_ACTIVE__) {
       console.log('[AudioSessionManager] Native audio active → skip deactivate');
     } else {
       backgroundAudioHelper.deactivate();
     }
   }
   }
  
  /**
   * BUILD 52.8: Check if any owner is currently playing
   */
  isAnyPlaying() {
    return Object.values(this.playingState).some(playing => playing);
  }
  
  /**
   * BUILD 52.8: Check if a specific owner is playing
   */
  isOwnerPlaying(owner) {
    return this.playingState[owner] === true;
  }

  /**
   * Acquire the audio session for a specific owner
   * This will stop any other active audio first (iOS requirement)
   * 
   * @param {string} owner - 'radio' | 'therapy' | 'soundscape'
   * @returns {Promise<boolean>} - true if session acquired successfully
   */
  async acquireSession(owner) {
    console.log(`[AudioSessionManager] Acquiring session for: ${owner} (current: ${this.currentOwner})`);
    
    // Activate background audio keep-alive (Capacitor native only)
    backgroundAudioHelper.activate();
    
    // If same owner, already have the session
    if (this.currentOwner === owner) {
      console.log(`[AudioSessionManager] ${owner} already owns the session`);
      return true;
    }
    
    // iOS: Must stop other audio before starting new one
    if (this.isIOS && this.currentOwner) {
      console.log(`[AudioSessionManager] iOS: Stopping ${this.currentOwner} before starting ${owner}`);
      await this.stopCurrentOwner();
    }
    
    // Even on non-iOS, we should stop other audio to prevent overlap
    if (this.currentOwner && this.currentOwner !== owner) {
      console.log(`[AudioSessionManager] Stopping ${this.currentOwner} before starting ${owner}`);
      await this.stopCurrentOwner();
    }
    
    this.currentOwner = owner;
    console.log(`[AudioSessionManager] Session acquired by: ${owner}`);
    return true;
  }

  /**
   * Release the audio session
   * BUILD 52.8: Now includes protection against background release
   * 
   * @param {string} owner - The owner requesting release
   * @param {boolean} force - If true, force release even if playing (default: false)
   */
  releaseSession(owner, force = false) {
    // BUILD 52.8: Guard against releasing session while playing in background
    if (!force && this.isOwnerPlaying(owner)) {
      console.log(`[AudioSessionManager] ⚠️ NOT releasing session: ${owner} is still playing`);
      console.log(`[AudioSessionManager] ⚠️ This is likely a background/unmount cleanup - ignoring`);
      return false;
    }
    
    if (this.currentOwner === owner) {
      console.log(`[AudioSessionManager] Session released by: ${owner}`);
      this.currentOwner = null;
      this.playingState[owner] = false;
      return true;
    }
    return false;
  }

  /**
   * Stop the current owner's audio
   * @private
   */
  async stopCurrentOwner() {
    if (!this.currentOwner) return;
    
    const stopCallback = this.stopCallbacks[this.currentOwner];
    if (stopCallback) {
      try {
        console.log(`[AudioSessionManager] Calling stop callback for: ${this.currentOwner}`);
        await stopCallback();
        
        // Small delay to let iOS audio system settle
        if (this.isIOS) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`[AudioSessionManager] Error stopping ${this.currentOwner}:`, error);
      }
    }
    
    this.playingState[this.currentOwner] = false;
    this.currentOwner = null;
  }

  /**
   * Check if a specific owner has the session
   */
  hasSession(owner) {
    return this.currentOwner === owner;
  }

  /**
   * Get the current session owner
   */
  getCurrentOwner() {
    return this.currentOwner;
  }

  /**
   * Force release all sessions (for cleanup)
   * Only use this for explicit user action (not background transitions)
   */
  async forceReleaseAll() {
    console.log(`[AudioSessionManager] Force releasing all sessions`);
    await this.stopCurrentOwner();
    this.currentOwner = null;
    this.playingState = { radio: false, therapy: false, soundscape: false, sos_calm: false };
    backgroundAudioHelper.deactivate();
  }
  
  /**
   * BUILD 52.8: Check if it's safe to cleanup (not playing)
   * Use this before cleanup to prevent stopping background audio
   */
  canSafelyCleanup(owner) {
    const isPlaying = this.isOwnerPlaying(owner);
    const inBackground = this.isAppInBackground;
    
    if (isPlaying && inBackground) {
      console.log(`[AudioSessionManager] ⚠️ Cannot safely cleanup ${owner}: playing in background`);
      return false;
    }
    
    return true;
  }
}

// Singleton instance
export const audioSessionManager = new AudioSessionManager();

export default audioSessionManager;
